import { Spacing } from '@vkontakte/vkui';
import classNames from 'clsx';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { xray } from 'lib/xray';
import React, { type ReactElement, memo, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actionTermsLink, OLD_ACTION_TERMS_LINK } from 'reactApp/appHelpers/actionSettings';
import { IS_IOS_MOBILE, IS_MOBILE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { isDarkTariffsPages, isRebranding, isRebrandingQuotaLanding, promotionEndDate } from 'reactApp/appHelpers/featuresHelpers';
import { promoQuotaAllTariffsLink } from 'reactApp/appHelpers/featuresHelpers/features/allTariffsLink';
import { isPromoQuotaWithLitress, litresEndDate } from 'reactApp/appHelpers/featuresHelpers/features/litres';
import { mailSpaceBenifits } from 'reactApp/appHelpers/featuresHelpers/features/mailSpaceBenefits';
import { mayPromo2025 } from 'reactApp/appHelpers/featuresHelpers/features/mayPromo2025';
import { multiTariffMobile } from 'reactApp/appHelpers/featuresHelpers/features/multiTariffMobile';
import { septemberPromo2025 } from 'reactApp/appHelpers/featuresHelpers/features/septemberPromo2025';
import { getQueryParams } from 'reactApp/appHelpers/settingsHelpers';
import { useDarkTheme } from 'reactApp/hooks/useDarkTheme';
import { clearRebrandingQuota, setRebrandingQuota } from 'reactApp/hooks/useRebranding';
import { checkMobileWebviewPayAvailable } from 'reactApp/modules/billing/billing.helpers';
import { loadQuotaTariffsLandingRequest, sendShowLandingAnalyticsAction } from 'reactApp/modules/quotaLanding/quotaLanding.module';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { isUserOverquotaAndFromApiNew } from 'reactApp/modules/userQuota/overquota.helpers';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { useRenewFromQuery } from 'reactApp/sections/MobileQuotaTariffsLanding/hooks/useRenewFromQuery';
import {
    useAddCardModal,
    useRemoveCardModal,
    useSubscriptionsModal,
} from 'reactApp/sections/MobileSubscriptionsPage/MobileSubscriptionsPage.hooks';
import { MayPromo2025UnlimitBlock } from 'reactApp/sections/QuotaLanding/BeautifulIllustrations/BeautifulIllustrations';
import { Container } from 'reactApp/sections/QuotaLanding/Container/Container';
import { useAutoUploadOnboardingFromQuery } from 'reactApp/sections/QuotaLanding/hooks/useAutoUploadOnboardingFromQuery';
import { useAutoUploadPromoBlock } from 'reactApp/sections/QuotaLanding/hooks/useAutoUploadPromoBlock';
import { useProductFromQuery } from 'reactApp/sections/QuotaLanding/hooks/useProductFromQuery';
import { MultiTariffsListMobile } from 'reactApp/sections/QuotaLanding/MultiTariffsListMobile/MultiTariffsListMobile';
import { PromoCounter } from 'reactApp/sections/QuotaLanding/PromoCounter/PromoCounter';
import { sendQuotaGa } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { TariffsList } from 'reactApp/sections/QuotaLanding/TariffsList/TairiffsList';
import { Button } from 'reactApp/ui/Button/Button';
import { ButtonLink } from 'reactApp/ui/ButtonLink/ButtonLink';
import Content from 'reactApp/ui/Content/Content';
import { renderSubscriptionsModal } from 'reactApp/ui/Mobile/SubscriptionsModal/SubscriptionsModal.helpers';
import { sendIosFavorableTariffsAnalytics, sendProfitableTariffsFirstAnalytics } from 'reactApp/utils/analytics';
import { defaultDateFormat } from 'reactApp/utils/formatDate';
import opener from 'reactApp/utils/opener';
import { sendTmrGoal, TMR3473039 } from 'reactApp/utils/tmr';

import { useVKIDFromQuery } from '../QuotaLanding/hooks/useVKIDFromQuery';
import { FeaturesCarousel } from './FeaturesCarousel/FeaturesCarousel';
import { Header } from './Header/Header';
import styles from './MobileQuotaTariffsLanding.css';
import { sendWebviewAnalytics } from './MobileQuotaTariffsLanding.helpers';

const HELP_LINK = 'https://help.mail.ru/cloud_web/size/increase';

const queryParams = getQueryParams();

export const MobileQuotaTariffsLanding = memo((): ReactElement => {
    const dispatch = useDispatch();

    const { isLoading, hasError, isLoaded } = useSelector(QuotaLandingSelectors.getState);
    const isAnonymous = useSelector(UserSelectors.isAnonymous);
    const isUserWithBilling = useSelector(UserSelectors.isUserWithBilling);
    const tariffsRef = useRef<HTMLDivElement>(null);

    const isOverQuotaCloud = useSelector(UserSelectors.isOverQuotaUser);
    const isOverquotaFromProfileApi = useSelector(isUserOverquotaAndFromApiNew);
    const isOverQuotaMailAndCloud = useSelector(UserQuotaSelectors.isOverquota);
    const isOverQuotaUser = isOverQuotaCloud || isOverquotaFromProfileApi || isOverQuotaMailAndCloud;
    const mayPromo2025Enabled = mayPromo2025 && !isOverQuotaUser;
    const isMultiTariff = multiTariffMobile && multiTariffMobile !== 'a';
    const septemberPromo2025Enabled = septemberPromo2025 && !isOverQuotaUser && queryParams?.promo !== 'false';

    const isMobilePayAvailable = checkMobileWebviewPayAvailable();

    const autouploadPromoBlock = useAutoUploadPromoBlock({
        isMobilePayAvailable,
        isIOSPromo: IS_IOS_MOBILE,
        mayPromo2025Enabled,
    });

    useAutoUploadOnboardingFromQuery();
    useVKIDFromQuery();
    useProductFromQuery({
        forceMobile: true,
        mobileInfo: {
            isMobilePayAvailable,
            isIOSPromo: IS_IOS_MOBILE,
        },
    });
    useRenewFromQuery({
        isMobilePayAvailable,
        isIOSPromo: IS_IOS_MOBILE,
    });
    useRemoveCardModal();
    useAddCardModal();
    useSubscriptionsModal();

    useDarkTheme(isDarkTariffsPages);

    const onSubscriptionsClick = useCallback(() => {
        sendQuotaGa({ action: 'subscriptions', label: 'click' });
        renderSubscriptionsModal({ isRebrandingQuotaLanding, isRebranding });
    }, []);

    const onCancelInfo = useCallback(() => {
        sendQuotaGa({ action: 'cancel-info', label: 'click' });
    }, []);

    useEffect(() => {
        dispatch(loadQuotaTariffsLandingRequest());
        emitAnalyticEvent('MOBILE_LANDING_SHOWN');
        sendIosFavorableTariffsAnalytics(['landing_view']);
        sendProfitableTariffsFirstAnalytics(['landing_view']);
        sendTmrGoal({ id: TMR3473039, goal: 'landing-view' });
    }, []);

    const retry = useCallback(() => {
        dispatch(loadQuotaTariffsLandingRequest());
    }, [dispatch]);

    useEffect(() => {
        if (isLoaded) {
            sendWebviewAnalytics('open', isMobilePayAvailable);
            dispatch(sendShowLandingAnalyticsAction());
        }
    }, [isLoaded]);

    useEffect(() => {
        const { action = null } = getQueryParams();
        if (action === 'openSubsModal') {
            renderSubscriptionsModal({ isRebrandingQuotaLanding, isRebranding });
        }
    }, []);

    useEffect(() => {
        if (isRebrandingQuotaLanding) {
            setRebrandingQuota();
        }

        return () => clearRebrandingQuota();
    }, [isRebrandingQuotaLanding]);

    // если пользователю недоступен билинг
    // если аноним
    // если не тач
    // редирект к хомяк
    if (!isUserWithBilling || isAnonymous || !IS_MOBILE_BROWSER) {
        xray.send('mobile_quota_tariffs_to_home');
        opener('/', true);
    }

    const scrollToTariffs = useCallback(() => {
        tariffsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    return (
        <Content
            buttonText="Попробовать ещё раз"
            retry={retry}
            isLoading={isLoading}
            hasError={hasError}
            isPage
            pageName="quota_landing_tariffs"
        >
            <div
                className={classNames(styles.root, {
                    [styles.root_dark]: isDarkTariffsPages,
                    [styles.root_rebranding]: isRebrandingQuotaLanding,
                    [styles.root_mayPromo2025]: mayPromo2025Enabled,
                    [styles.root_mayPromo2025_compact]: mayPromo2025Enabled && isMultiTariff,
                    [styles.root_septemberPromo2025]: septemberPromo2025Enabled,
                })}
            >
                {!(mayPromo2025Enabled && isMultiTariff && septemberPromo2025Enabled) && (
                    <>
                        <Spacing size={septemberPromo2025Enabled ? 32 : 24} />
                        <Header septemberPromo2025Enabled={septemberPromo2025Enabled} />
                        <Spacing size={autouploadPromoBlock || mayPromo2025Enabled ? 20 : septemberPromo2025Enabled ? 8 : 32} />
                    </>
                )}
                {mayPromo2025Enabled && isMultiTariff && <Spacing size={48} />}
                {mayPromo2025Enabled && (
                    <>
                        <div
                            className={classNames({
                                [styles.headerBlock]: isMultiTariff,
                            })}
                        >
                            {isMultiTariff && (
                                <div className={styles.title}>
                                    Тарифы <br /> Mail Space
                                </div>
                            )}

                            <PromoCounter
                                isIOSPromo={IS_IOS_MOBILE}
                                isAndroidPromo={!IS_IOS_MOBILE}
                                isMobilePayAvailable={isMobilePayAvailable}
                                mayPromo2025Enabled={mayPromo2025Enabled}
                                isCompact={isMultiTariff}
                            />
                        </div>
                        <Spacing size={isMultiTariff ? 32 : 20} />
                    </>
                )}

                {septemberPromo2025Enabled && (
                    <>
                        <div className={styles.headerBlockPromo}>
                            <PromoCounter
                                isIOSPromo={IS_IOS_MOBILE}
                                isAndroidPromo={!IS_IOS_MOBILE}
                                isMobilePayAvailable={isMobilePayAvailable}
                                septemberPromo2025Enabled={septemberPromo2025Enabled}
                            />
                        </div>
                        <Spacing size={20} />
                    </>
                )}

                {/* если акция, то не будет рендерится*/}
                {autouploadPromoBlock && (
                    <>
                        {autouploadPromoBlock}
                        <Spacing size={4} />
                    </>
                )}
                {isMultiTariff ? (
                    <MultiTariffsListMobile
                        ref={tariffsRef}
                        isAnonymous={isAnonymous}
                        isTariffsPage={true}
                        isIOSPromo={IS_IOS_MOBILE}
                        isMobilePayAvailable={isMobilePayAvailable}
                        isMobilePayFromGetParam={false}
                        mayPromo2025Enabled={mayPromo2025Enabled}
                    />
                ) : (
                    <TariffsList
                        ref={tariffsRef}
                        isIOSPromo={IS_IOS_MOBILE}
                        isMobilePayAvailable={isMobilePayAvailable}
                        isMobilePayFromGetParam={false}
                        isAnonymous={isAnonymous}
                        minTariff={queryParams?.min_tariff}
                        isTariffsPage
                        isPhone
                        mayPromo2025Enabled={mayPromo2025Enabled}
                    />
                )}
                {mayPromo2025Enabled && (
                    <Container isNarrow>
                        <Spacing size={60} />
                        <MayPromo2025UnlimitBlock scrollToTariffs={scrollToTariffs} />
                    </Container>
                )}
                {/* tempexp_17906-next-line */}
                {mailSpaceBenifits !== 'a' ? <FeaturesCarousel /> : <Spacing size={isRebrandingQuotaLanding ? 38 : 20} />}
                <Container isNarrow>
                    <Button className={classNames(styles.button)} theme="vk" onClick={onSubscriptionsClick} qaId="subscriptions">
                        Мои подписки
                    </Button>
                    <Spacing size={isRebrandingQuotaLanding ? 10 : 12} />
                    <Button
                        className={classNames(styles.button)}
                        theme="vk"
                        href={actionTermsLink || OLD_ACTION_TERMS_LINK}
                        target="_blank"
                        qaId="action"
                    >
                        Полные условия акции
                    </Button>
                    {/* tempexp_17509-start */}
                    {isPromoQuotaWithLitress && !!litresEndDate && (
                        <div className={styles.actionDatesBlock}>
                            <span className={styles.actionDates}>Срок акции с 13.09.2024 по {litresEndDate}</span>
                        </div>
                    )}
                    {/* tempexp_17509-end */}
                    {/* tempexp_18474-start */}
                    {mayPromo2025Enabled && !!promotionEndDate && (
                        <div className={styles.actionDatesBlock}>
                            <span className={styles.actionDates}>
                                Срок акции с 09.06.25 по {format(parseISO(promotionEndDate), defaultDateFormat)}
                            </span>
                        </div>
                    )}
                    {/* tempexp_18474-end */}
                    <Spacing size={isRebrandingQuotaLanding ? 10 : 20} />
                    <ButtonLink
                        className={styles.link}
                        href={HELP_LINK}
                        target="_blank"
                        rel="noopener"
                        hoverUnderline={false}
                        onClick={onCancelInfo}
                    >
                        Как отменить подписку?
                    </ButtonLink>
                    {Boolean(promoQuotaAllTariffsLink) && (
                        <ButtonLink
                            className={styles.link}
                            href={promoQuotaAllTariffsLink}
                            target="_blank"
                            rel="noopener"
                            hoverUnderline={false}
                        >
                            Все тарифы
                        </ButtonLink>
                    )}
                </Container>
                <Spacing size={isRebrandingQuotaLanding ? 32 : 40} />
            </div>
        </Content>
    );
});

MobileQuotaTariffsLanding.displayName = 'MobileQuotaTariffsLanding';
